import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, message, Modal, Tooltip } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CheckOutlined } from '@ant-design/icons';
import { useTaskListStore } from './tasklist_store';
import { ConflictStatusResponse } from '../../../model/conflict_status_response';

import styles from './index.module.scss';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 競合チェックボタン
 * @param searchTime
 * @constructor
 */
export const ConflictButton: React.FC<{ searchTime?: dayjs.Dayjs }> = ({ searchTime }) => {
  const { checkConflict, getConflictStatus } = useTaskListStore((selector) => selector);
  const [status, setStatus] = useState<ConflictStatusResponse>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<number>(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [lastSearchTime, setLastSearchTime] = useState<dayjs.Dayjs>(searchTime ?? dayjs());

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getConflictStatus()
      .then((resp) => {
        setStatus(resp);
        setChecking(resp?.isRunning ?? false);
      })
      .catch((e) => {
        if (e instanceof ApiError) {
          setErrorCode(e.response.status);
        } else {
          setErrorCode(500);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [lastSearchTime]);

  useEffect(() => {
    setLastSearchTime(searchTime);
  }, [searchTime]);

  const handleCheck = () => {
    setIsModalOpen(false);
    setLoading(true);
    checkConflict()
      .then(() => {
        messageApi.success(Messages.EXECUTE_CONFLICT_CHECK);
        setChecking(true);
      })
      .catch((e) => {
        if (e instanceof ApiError) {
          if (e.response.status === 409) {
            setChecking(true);
            messageApi.error(Messages.ERR_DIALOG_CHECK_CONFLICT);
            return;
          }
          if (e.response.status === -1) {
            window.alert(Messages.ERR_DIALOG_TIMEOUT);
            return;
          }
        }
        messageApi.error(Messages.ERR_OTHER);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const lastDate = useMemo(() => {
    if (status === undefined || status.checkTime === undefined) {
      return '-';
    }
    return dayjs(status.checkTime).format('YYYY/MM/DD HH:mm:ss');
  }, [status]);

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  if (errorCode === -1) {
    return (
        <div className={styles.conflictBox}>
          <Alert message={Messages.ERR_DIALOG_TIMEOUT} type="error" />
        </div>
    );
  }

  if (errorCode > 0) {
    return (
      <div className={styles.conflictBox}>
          <Alert message={Messages.ERR_OTHER} type="error" />
      </div>
    );
  }

  return (
        <div className={styles.conflictBox}>
            {contextHolder}
            <Button loading={loading || checking} type={'primary'} size={'middle'} onClick={() => { setIsModalOpen(true); }}>{checking ? '競合チェック実施中' : '競合チェック（手動）'}</Button>
            <div style={{ marginLeft: '10px' }}>
              最終競合チェック日時: {lastDate}&nbsp;&nbsp;&nbsp;&nbsp;
              <Tooltip title={<>
                <CheckOutlined style={{ color: '#0e0' }}/>: 競合なし<br/>
                <span style={{
                  color: '#f00',
                  fontSize: '1.2em',
                }}>※</span>: 競合の可能性あり
              </>}>
                <Button>凡例</Button>
              </Tooltip>
            </div>
            <Modal title="競合チェック" open={isModalOpen} onOk={handleCheck} onCancel={handleCancel} footer={[
              <Button key="back" onClick={handleCancel}>
                キャンセル
              </Button>,
              <Button key="submit" type="primary" onClick={handleCheck}>
                チェック開始
              </Button>,
            ]}>
              <p>急ぎの際は、ボタンを押すと最新のデータで競合確認を開始します。</p>
              <p>ボタンを押した後は、時間をおいて再び検索ボタンを押し、最新の競合確認結果を取得してください。</p>
            </Modal>
        </div>
  );
};
